import React from "react";
import { Container, Row, Col, FormGroup, Button } from 'reactstrap';
import writeXlsxFile from 'write-excel-file';
import DatePicker from "../Form/DatePicker";
import { FormLabel } from "../Form/FormLabel";
import moment from 'moment';
import { useState, useEffect } from "react";
import { endpoints } from "../../utils/Constants";
import LoadingAnimation from "../Form/LoadingAnimation";
import useFetch from "../../Services/useFetch";

const ExcelExport = ({ jobTracker, data }) => {
    const thisQuarter = parseInt((new Date().getMonth() + 1) / 3);
    const year = thisQuarter === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();
    const lastQuarter = thisQuarter === 0 ? 4 : thisQuarter - 1;
    const fromDate = new Date(year, (lastQuarter * 3) - 1, 1);
    const toDate = new Date(year, lastQuarter * 3 + 2, 1);
    const [from, setFrom] = useState(moment(fromDate));
    const [to, setTo] = useState(moment(toDate));
    const [reportData, loading, error, refresh] = useFetch(!data && jobTracker ? endpoints.charterReport + from.format('YYYY-MM-DDTHH:mm:ss') + "/" + to.format('YYYY-MM-DDTHH:mm:ss') : null, {}, false);
    const [reportDetails, setReportDetails] = useState();

    const handleChangeFrom = (e) => { setFrom(moment(e)); setReportDetails(); };
    const handleChangeTo = (e) => { setTo(moment(e)); setReportDetails(); };

    useEffect(() => { data && setReportDetails(data); }, [data]);
    useEffect(() => { reportData && setReportDetails(reportData); }, [reportData]);

    const getQuarter = (d) => { const q = d && d.getMonth ? parseInt((d.getMonth() + 1) / 3) : 1; return (d && d.getFullYear ? d.getFullYear() : year) + " - Q" + q; };
    const getPax = (detail) => (detail.manifest && detail.manifest.length) ? detail.manifest.length : Math.max(detail.route[0].passengerNumber, detail.route[0].passengerRange_Max);
    const getWeight = (detail) => detail.manifest ? detail.manifest.reduce((a, x) => a + (x.freightWeight || 0) + (x.cargoWeight || 0) + (x.passengerWeight || 0), 0) : 0;

    const buildRows = () => {
        if (!reportDetails) return [];
        if (jobTracker) {
            return reportDetails.map(detail => ({
                Created: detail.request?.createdAt,
                JB: detail.request?.jobReference,
                Type: detail.request?.charterSubType,
                Charter: detail.request?.qGov ? 'QG' : 'STD',
                ClientDepartment: detail.clientDepartment?.departmentName,
                Client: detail.clientStaff ? detail.clientStaff.fullName : detail.clientDepartment ? detail.clientDepartment.fullName : detail.client?.fullName,
                OutDate: detail.route ? detail.route[0]?.departTime : detail.request?.departTime,
                Departing: detail.route?.[0]?.from,
                RtnDate: detail.route?.[detail.route.length - 1]?.departTime,
                Arriving: detail.route?.[detail.route.length - 1]?.to,
                JobStatus: detail.request?.status,
                WinningOperator: detail?.bid?.supplier?.companyName,
                BidDate: detail?.bid?.createdAt,
                BUY: detail?.bid?.price,
                SELL: detail?.bid ? (detail.bid.price > detail.bid.withMargin ? detail.bid.price + detail.bid.withMargin : detail.bid.withMargin) : undefined,
                Margin: detail?.bid ? (detail.bid.price > detail.bid.withMargin ? detail.bid.withMargin : (detail.bid.withMargin - (detail.bid.price || 0))) : undefined,
                FinalPAX: getPax(detail),
                FinalWeight: getWeight(detail),
                BookedQuarter: getQuarter(new Date(detail.request.createdAt)),
                OperatingQuarter: getQuarter(new Date(detail.route[0]?.departTime))
            }));
        }
        return reportDetails.map(detail => ({
            Status: detail.request.status,
            JobReference: detail.request.jobReference,
            PONumber: detail.request.purchaseOrderNumber,
            DepartTime: detail.request.departTime,
            From: detail.request.from,
            To: detail.request.to,
            Remaining: detail.request.createdAt,
            Sectors: detail.request.routeCount,
            Bids: detail.request.bidCount,
            SpecialRequests: detail.request.specialRequests
        }));
    };

    const jobTrackerSchema = [
        { column: 'Created', type: Date, value: r => r.Created ? new Date(r.Created) : null },
        { column: 'JB', type: String, value: r => r.JB },
        { column: 'Type', type: String, value: r => r.Type },
        { column: 'Charter', type: String, value: r => r.Charter },
        { column: 'Client Dept', type: String, value: r => r.ClientDepartment || '' },
        { column: 'Client', type: String, value: r => r.Client || '' },
        { column: 'Out Date', type: Date, value: r => r.OutDate ? new Date(r.OutDate) : null },
        { column: 'Departing', type: String, value: r => r.Departing || '' },
        { column: 'Return Date', type: Date, value: r => r.RtnDate ? new Date(r.RtnDate) : null },
        { column: 'Arriving', type: String, value: r => r.Arriving || '' },
        { column: 'Status', type: String, value: r => r.JobStatus || '' },
        { column: 'Winning Operator', type: String, value: r => r.WinningOperator || '' },
        { column: 'Bid Date', type: Date, value: r => r.BidDate ? new Date(r.BidDate) : null },
        { column: 'BUY', type: Number, value: r => r.BUY ?? null },
        { column: 'SELL', type: Number, value: r => r.SELL ?? null },
        { column: 'Margin', type: Number, value: r => r.Margin ?? null },
        { column: 'Final PAX', type: Number, value: r => r.FinalPAX ?? null },
        { column: 'Final Weight', type: Number, value: r => r.FinalWeight ?? null },
        { column: 'Booked Quarter', type: String, value: r => r.BookedQuarter },
        { column: 'Operating Quarter', type: String, value: r => r.OperatingQuarter }
    ];

    const charterListSchema = [
        { column: 'Status', type: String, value: r => r.Status },
        { column: 'Job Reference', type: String, value: r => r.JobReference },
        { column: 'PO Number', type: String, value: r => r.PONumber || '' },
        { column: 'Depart Time', type: Date, value: r => r.DepartTime ? new Date(r.DepartTime) : null },
        { column: 'From', type: String, value: r => r.From || '' },
        { column: 'To', type: String, value: r => r.To || '' },
        { column: 'Remaining', type: Date, value: r => r.Remaining ? new Date(r.Remaining) : null },
        { column: 'Sectors', type: Number, value: r => r.Sectors ?? null },
        { column: 'Bids', type: Number, value: r => r.Bids ?? null },
        { column: 'Special Requests', type: String, value: r => r.SpecialRequests || '' }
    ];

    const download = async () => {
        const rows = buildRows();
        if (!rows.length) return;
        const schema = jobTracker ? jobTrackerSchema : charterListSchema;
        await writeXlsxFile(rows, { schema, fileName: jobTracker ? 'job-history.xlsx' : 'charter-list.xlsx', dateFormat: 'yyyy-mm-dd HH:MM:ss' });
    };

    return <Container>
        {jobTracker ? <Row>
            <Col><FormGroup><FormLabel>From</FormLabel><DatePicker disabled={loading} type='datetime-local' placeholder="Report From" onChange={handleChangeFrom} selected={moment(from).toDate()} /></FormGroup></Col>
            <Col><FormGroup><FormLabel>To</FormLabel><DatePicker disabled={loading} type='datetime-local' placeholder="Report To" onChange={handleChangeTo} selected={moment(to).toDate()} /></FormGroup></Col>
        </Row> : null}
        {loading ? <LoadingAnimation /> : jobTracker && !reportDetails ? <Button onClick={refresh}>Load</Button> : null}
        {error ? <h5>Error Loading Report</h5> : null}
        {reportDetails ? <Button type='button' onClick={download}>Download Data</Button> : null}
    </Container>;
};

export default ExcelExport;
